import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { 
  Users, Image, Bug, CreditCard, TrendingUp, 
  UserPlus, DollarSign, AlertCircle 
} from 'lucide-react';
import { adminFetch } from './AdminLayout';

const StatCard = ({ icon: Icon, label, value, subtext, color = 'emerald', link }) => {
  const Content = (
    <div className={`bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-${color}-500/50 transition-colors`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {subtext && <p className="text-gray-500 text-sm mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 bg-${color}-600/20 rounded-lg`}>
          <Icon className={`w-6 h-6 text-${color}-500`} />
        </div>
      </div>
    </div>
  );

  if (link) {
    return <Link to={link}>{Content}</Link>;
  }
  return Content;
};

const AdminDashboard = () => {
  const { adminToken } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await adminFetch('/admin/dashboard/stats', {}, adminToken);
        setStats(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [adminToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">MandalaMuse admin overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats?.total_users || 0}
          subtext={`+${stats?.recent_signups || 0} this week`}
          color="emerald"
          link="/admin/users"
        />
        <StatCard
          icon={Image}
          label="Total Artworks"
          value={stats?.total_artworks || 0}
          color="blue"
          link="/admin/artworks"
        />
        <StatCard
          icon={Bug}
          label="Open Bugs"
          value={stats?.open_bugs || 0}
          color="orange"
          link="/admin/bugs"
        />
        <StatCard
          icon={CreditCard}
          label="Total Credits"
          value={stats?.total_credits || 0}
          subtext="across all users"
          color="purple"
        />
      </div>

      {/* Revenue & Transactions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-600/20 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-white">Revenue</h3>
          </div>
          <p className="text-4xl font-bold text-white">
            ${(stats?.total_revenue || 0).toFixed(2)}
          </p>
          <p className="text-gray-400 text-sm mt-1">Total lifetime revenue</p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-600/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-white">Transactions</h3>
          </div>
          <p className="text-4xl font-bold text-white">
            {stats?.total_transactions || 0}
          </p>
          <p className="text-gray-400 text-sm mt-1">Total payment transactions</p>
          <Link 
            to="/admin/transactions" 
            className="text-emerald-400 text-sm hover:underline mt-2 inline-block"
          >
            View all transactions →
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link 
            to="/admin/users"
            className="flex items-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
          >
            <UserPlus size={18} />
            <span>Manage Users</span>
          </Link>
          <Link 
            to="/admin/bugs"
            className="flex items-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
          >
            <Bug size={18} />
            <span>View Bugs</span>
          </Link>
          <Link 
            to="/admin/email"
            className="flex items-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
          >
            <Users size={18} />
            <span>Email Users</span>
          </Link>
          <Link 
            to="/admin/artworks"
            className="flex items-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
          >
            <Image size={18} />
            <span>Browse Art</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
